import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import axios from 'axios';
import { Box, Typography, Button, Alert, Chip, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Stop as StopIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';
// Le WS de support est servi par backoffice_server lui-même (relais vers qrcode_server), pas un
// microservice séparé — même host/port que l'API REST, juste en ws(s):// et /ws/support.
const WS_URL = API_URL.replace(/^http/, 'ws') + '/ws/support';
const QRCODE_CLIENT_URL = process.env.REACT_APP_QRCODE_CLIENT_URL || 'http://localhost:3104';

// Panneau de contrôle d'une session de support live (voir plan steady-watching-leaf.md).
// Ancienne version : rejeu vidéo rrweb-player, avec quelques secondes de décalage inhérentes au
// modèle enregistrer -> transmettre -> rejouer. Nouvelle version : l'admin ouvre directement la
// vraie application (qr_generator) via un token d'impersonation scopé à cette session -- plus de
// vidéo, plus de décalage. Cette page ne fait plus que : afficher le statut, basculer
// Observation/Contrôle (devenu une vraie règle d'accès lecture/écriture côté qrcode_server, pas
// seulement une indication UI), et donner accès à l'application cliente en un clic. rrweb continue
// de tourner et d'enregistrer côté client (voir SupportSessionListener.js), uniquement pour la
// preuve d'accès -- cette page ne le consomme plus.
export default function SupportSessionPage() {
    const { id, sessionToken } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { accessToken } = useContext(TokenContext);

    const wsRef = useRef(null);
    // Token d'impersonation co-navigation, reçu via navigation state depuis ClientManagementPage
    // (jamais dans l'URL/l'historique, voir plan) -- absent si la page est rechargée directement
    // (state React Router non persistant), auquel cas le bouton "Ouvrir l'application cliente"
    // reste désactivé plutôt que de planter.
    const supportAccessToken = location.state?.accessToken || null;

    const [status, setStatus] = useState('connecting'); // connecting | live | ended | error
    const [mode, setMode] = useState('observation');
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        if (!accessToken || !sessionToken) return;

        const ws = new WebSocket(`${WS_URL}?token=${accessToken}&role=admin&sessionToken=${sessionToken}`);
        wsRef.current = ws;

        // Même garde d'identité que côté qr_generator (SupportSessionListener.js) : sans elle,
        // une ancienne connexion avortée par le double-montage React StrictMode peut modifier
        // status/errorMsg APRÈS que la connexion suivante (la bonne) a déjà ouvert -- un ws1 qui
        // se ferme en retard repasserait status à 'error' même si ws2 tourne parfaitement bien.
        ws.onopen = () => { if (wsRef.current === ws) { setStatus('live'); setErrorMsg(null); } };
        ws.onerror = () => { if (wsRef.current === ws) { setStatus('error'); setErrorMsg('Connexion WebSocket impossible.'); } };
        ws.onclose = () => { if (wsRef.current === ws) setStatus((s) => (s === 'ended' ? s : 'error')); };

        ws.onmessage = (evt) => {
            let msg;
            try { msg = JSON.parse(evt.data); } catch (e) { return; }

            if (msg.type === 'mode_change') { setMode(msg.mode); return; }
            if (msg.type === 'session_ended') { setStatus('ended'); return; }
        };

        return () => { ws.close(); };
    }, [accessToken, sessionToken]);

    const toggleMode = (_e, newMode) => {
        if (!newMode || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ type: 'set_mode', mode: newMode }));
    };

    const endSession = () => {
        const token = accessToken;
        axios.post(`${API_URL}/api/clients/${id}/support/${sessionToken}/end`, {}, { headers: { Authorization: `Bearer ${token}` } })
            .finally(() => setStatus('ended'));
    };

    const openClientApp = () => {
        if (!supportAccessToken) return;
        const url = `${QRCODE_CLIENT_URL}?supportToken=${encodeURIComponent(supportAccessToken)}&supportSession=${encodeURIComponent(sessionToken)}`;
        window.open(url, '_blank', 'noopener');
    };

    return (
        <Box sx={{ p: 3, flex: 1, minWidth: 0 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/clients/${id}`)} sx={{ mb: 2 }}>
                Retour à la fiche client
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h5">Session de support live</Typography>
                <Chip
                    label={status === 'live' ? 'En direct' : status === 'connecting' ? 'Connexion...' : status === 'ended' ? 'Terminée' : 'Erreur'}
                    color={status === 'live' ? 'success' : status === 'error' ? 'error' : 'default'}
                    size="small"
                />
                {status === 'live' && (
                    <Chip label={mode === 'control' ? 'Vous agissez sur le compte du client' : 'Lecture seule (observation)'}
                        color={mode === 'control' ? 'warning' : 'info'} size="small" />
                )}
            </Box>

            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

            {status === 'connecting' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            )}

            {status === 'live' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <ToggleButtonGroup value={mode} exclusive onChange={toggleMode} size="small">
                            <ToggleButton value="observation">Observation</ToggleButton>
                            <ToggleButton value="control">Prendre la main</ToggleButton>
                        </ToggleButtonGroup>
                        <Button variant="outlined" color="error" startIcon={<StopIcon />} onClick={endSession}>
                            Terminer la session
                        </Button>
                    </Box>

                    <Button variant="contained" startIcon={<OpenInNewIcon />} onClick={openClientApp} disabled={!supportAccessToken}>
                        Ouvrir l'application cliente
                    </Button>
                    {!supportAccessToken && (
                        <Alert severity="warning" sx={{ maxWidth: 600 }}>
                            Le token de session n'est plus disponible (page rechargée ?). Relancez une session de
                            support depuis la fiche client pour pouvoir ouvrir l'application cliente.
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
                        Ouvre un nouvel onglet chargé directement sur les données réelles du client (compte
                        {mode === 'observation' ? ' en lecture seule tant que la session reste en observation' : ' avec accès en écriture, la session est en mode contrôle'}).
                        Le curseur de chaque partie reste visible chez l'autre (halo orange) tant que la session
                        est ouverte.
                    </Typography>
                </Box>
            )}

            {status === 'ended' && <Alert severity="info">La session est terminée. Elle reste consultable dans l'historique.</Alert>}
        </Box>
    );
}
